import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { Link } from "../../../common/Link";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { ProductCompareTheme } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { IProductID, IWebPageURL } from "../../../models/nominals";
import { ICompareTile, IProductFlair } from "../../../models/product-compare";
import { getShopNowURL } from "../selectors";

import styles from "./ProductCompareTileHeader.module.scss";

const getVariantTitle = (
    selectedVariant: IProduct | null,
    data: ICompareTile,
): string => {
    return selectedVariant ? selectedVariant.title : data.rootProducts[0].title;
};

const getVariantURL = (
    selectedVariant: IProduct | null,
    data: ICompareTile,
): IWebPageURL | null => {
    return selectedVariant?.link
        ? selectedVariant?.link
        : data.rootProducts[0].link;
};

const calculateNumReviews = (data: ICompareTile): number => {
    return data.rootProducts.reduce((memo, rp) => memo + rp.num_reviews, 0);
};

const calculateAvgRating = (data: ICompareTile): number => {
    const total = data.rootProducts.reduce(
        (memo, rp) => memo + (rp.rating || 0),
        0,
    );
    const products = data.rootProducts.filter((rp) => rp.rating !== null);

    return total / products.length;
};

const buildRating = (
    showRating: boolean | undefined,
    selectedVariant: IProduct | null,
    numReviews: number,
    avgRating: number,
    theme: ProductCompareTheme | null | undefined,
    variantURL: IWebPageURL | null,
): React.ReactNode => {
    if (!showRating) {
        return null;
    }
    if (!selectedVariant) {
        return null;
    }
    if (numReviews < 1) {
        return null;
    }
    if (theme === ProductCompareTheme.SELECT) {
        return (
            <div className={styles.reviewLink}>
                <RatingGraphic
                    cardClass={styles.starRating}
                    cardSize="large"
                    numReviews={numReviews}
                    rating={avgRating}
                />
            </div>
        );
    }
    return (
        <a
            className={styles.reviewLink}
            href={`${variantURL}#reviews__body`}
            title={t`Read Reviews`}
        >
            <RatingGraphic
                cardClass={styles.starRating}
                cardSize="large"
                numReviews={numReviews}
                rating={avgRating}
            />
        </a>
    );
};

const renderHeader = (
    data: ICompareTile,
    selectedVariant: IProduct | null,
    pageRootProduct: IProductID | null | undefined,
    theme: ProductCompareTheme | null | undefined,
    variantTitle: string,
    numReviews: number,
    avgRating: number,
    showRating: boolean | undefined,
    variantURL: IWebPageURL | null,
): React.ReactNode => {
    const showSelectedFlair =
        theme === ProductCompareTheme.SELECT
            ? data.product_compare_tile.product_id === pageRootProduct
            : false;
    const selectedFlairData = {
        content: "SELECTED",
        style: "flair--selected",
    };
    const selectedFlair = <Flair flair={selectedFlairData} />;
    if (theme === ProductCompareTheme.SELECT) {
        return (
            <header className={styles.root}>
                {showSelectedFlair && selectedFlair}
                <Image
                    src={data.product_compare_tile.image.url}
                    alt={data.product_compare_tile.image.title}
                />
                {data.secondary_flair && <Flair flair={data.secondary_flair} />}
                <RichText
                    html={data.product_compare_tile.title}
                    className={`title ${styles.richText}`}
                />
            </header>
        );
    }
    const rootClasses = classNames({
        [styles.root]: true,
        [theme || ""]: true,
    });
    const showSecondFlairBelowRating =
        theme &&
        [ProductCompareTheme.STEP_UP, ProductCompareTheme.THEME_ONE].includes(
            theme,
        );

    return (
        <header className={rootClasses}>
            <Link
                href={getShopNowURL(data, selectedVariant)}
                title={interpolate(t`Learn more about %s`, [variantTitle])}
            >
                {showSelectedFlair && selectedFlair}
                {/* if it is a linked image, set alt attr as it describes the function of the link */}
                <Image
                    src={data.product_compare_tile.image.url}
                    alt={interpolate(t`Learn more about %s`, [variantTitle])}
                />
                {data.secondary_flair && !showSecondFlairBelowRating && (
                    <div className="secondary-flair">
                        <Flair flair={data.secondary_flair} />
                    </div>
                )}
                <RichText
                    html={data.product_compare_tile.title}
                    className={`title ${styles.richText} ${theme}`}
                />
            </Link>
            {buildRating(
                showRating,
                selectedVariant,
                numReviews,
                avgRating,
                theme,
                variantURL,
            )}
            {data.secondary_flair && showSecondFlairBelowRating && (
                <div className="secondary-flair">
                    <Flair flair={data.secondary_flair} />
                </div>
            )}
        </header>
    );
};

const renderPrimaryFlair = (
    flair: IProductFlair,
    theme: ProductCompareTheme | null | undefined,
    data: ICompareTile,
    selectedVariant: IProduct | null,
    variantTitle: string,
): React.ReactNode => {
    if (theme === ProductCompareTheme.STEP_UP) {
        return (
            <Link
                href={getShopNowURL(data, selectedVariant)}
                title={interpolate(t`Learn more about %s`, [variantTitle])}
            >
                <div className="primary-flair">
                    <Flair flair={flair} />
                </div>
            </Link>
        );
    }

    return <Flair flair={flair} />;
};

export const ProductCompareTileHeader = (props: {
    data: ICompareTile;
    selectedVariant: IProduct | null;
    pageRootProduct?: IProductID | null;
    theme?: ProductCompareTheme | null;
    showDescription?: boolean;
    showRating?: boolean;
}): React.ReactNode => {
    const variantTitle = getVariantTitle(props.selectedVariant, props.data);
    const variantURL = getVariantURL(props.selectedVariant, props.data);
    const numReviews = calculateNumReviews(props.data);
    const avgRating = calculateAvgRating(props.data);

    const descriptionClasses = classNames({
        [styles.description]: true,
        [styles.descriptionThemeStepUp]:
            props.theme === ProductCompareTheme.STEP_UP,
        [styles.descriptionThemeSelect]:
            props.theme === ProductCompareTheme.SELECT,
        [styles.descriptionThemeOne]:
            props.theme === ProductCompareTheme.THEME_ONE,
    });

    return (
        <>
            {props.data.flair &&
                renderPrimaryFlair(
                    props.data.flair,
                    props.theme,
                    props.data,
                    props.selectedVariant,
                    variantTitle,
                )}
            {renderHeader(
                props.data,
                props.selectedVariant,
                props.pageRootProduct,
                props.theme,
                variantTitle,
                numReviews,
                avgRating,
                props.showRating,
                variantURL,
            )}
            {props.showDescription && (
                <RichText
                    className={descriptionClasses}
                    html={props.data.product_compare_tile.short_description}
                    inline={true}
                />
            )}
        </>
    );
};
